import { ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { useFormikContext, getIn } from 'formik'
import { isEmpty } from 'lodash'
import { IAutocompleteOutput } from 'components/v2/Autocomplete/types'
import { Column } from 'components/v2/Column'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { CompanyLineAutocomplete } from 'features/Lines/components/CompanyLineAutocomplete'
import { IRouteForm, IRouteCardProps } from './types'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { routeFormUtils } from 'features/Routes/utils'
import { Label } from 'components/v2/Label'
import { Radio, RadioWrapper } from 'components/v2/Radio'
import { safeFormatMessage } from '@10xlabs/utils'
import { verticalMessages } from 'features/Companies/components/Vertical/messages'
import {
  GTBusinessClientAutocomplete,
  GTBusinessSiteSuggestion,
  GTCompanyV2VerticalInput,
  GTPlaceSuggestion
} from 'gql/types.generated'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { GET_LINES_BY_COMPANY } from 'features/Lines/queries-legacy'
import { useQuery } from '@apollo/client'
import { canSelectCompany, getDefaultCompanyForForm } from 'features/Login/utils'
import { useLogin } from 'features/Login/hooks'
import { RouteStops } from 'features/Routes/components/RouteStops/RouteStops'
import { DropResult } from 'react-beautiful-dnd'
import { CompanyV2Vertical } from 'gql/generated/graphql'

const VERTICALS_LIST = [
  GTCompanyV2VerticalInput.Travel,
  GTCompanyV2VerticalInput.City,
  GTCompanyV2VerticalInput.Team,
  GTCompanyV2VerticalInput.Rental
]
const messages = defineMessages({
  clientPlaceholder: {
    id: 'services.form.businessClient.placeholder',
    defaultMessage: 'Select business client'
  },
  sitePlaceholder: {
    id: 'services.form.businessSite.placeholder',
    defaultMessage: 'Select business site'
  },
  companyLabel: {
    id: 'services.form.company.label',
    defaultMessage: 'Company'
  },
  companyPlaceholder: {
    id: 'services.form.company.placeholder',
    defaultMessage: 'Select company'
  },
  companyLineLabel: {
    id: 'services.form.companyLine.label',
    defaultMessage: 'Company Line'
  },
  companyLinePlaceholder: {
    id: 'services.form.companyLine.placeholder',
    defaultMessage: 'Select a company line'
  }
})

const StyledRadioWrapper = styled(RadioWrapper)`
  gap: 8px 20px;
`

const RouteCardContainer = styled(FormLayout.Card)`
  gap: 24px;
`

const RouteStopsContainer = styled.div`
  border: 1px solid #ededed;
  border-radius: 16px;
  padding: 16px;
  gap: 16px;
  display: grid;
`

/* TODO: https://kolors.atlassian.net/browse/NA-13683 Reuse the form for creating a new route  */
export const RouteCard = ({ isEditDisabled, title }: IRouteCardProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const { values, setFieldValue, setValues, handleBlur, touched, errors, initialValues } =
    useFormikContext<IRouteForm>()

  const { data: linesByCompany, error: loadingCompanyLinesError } = useQuery(GET_LINES_BY_COMPANY, {
    variables: {
      companyId: values.company.id,
      limit: 1000
    },
    skip:
      values.vertical === GTCompanyV2VerticalInput.City ||
      values.vertical === GTCompanyV2VerticalInput.Travel ||
      !values.company.id,
    onCompleted: (data) => {
      setFieldValue('companyLine', data.companyLines[0])
    }
  })

  const onAddStop = (newStop: GTPlaceSuggestion): void => {
    const newValues = {
      ...values,
      stops: [...values.stops, newStop],
      encodedShape: '',
      distance: ''
    }
    setValues(newValues)
  }

  const onChangeStop = (place: GTPlaceSuggestion, stopIndex: number): TSFixMe => {
    const newStops = [...values.stops]
    newStops[stopIndex] = place
    const newValues = {
      ...values,
      stops: newStops,
      encodedShape: '',
      distance: ''
    }

    setValues(newValues)
  }

  const onDelete = (stopIndex: number): void => {
    const newStops = values.stops.filter((stop, index) => {
      return index !== stopIndex
    })
    const newValues = {
      ...values,
      encodedShape: '',
      distance: '',
      stops: newStops
    }

    setValues(newValues)
  }

  const handleDragEnd = (result: DropResult): void => {
    if (!result.destination) {
      return
    }
    const newStops = [...values.stops]
    const stopsReordered = newStops
    const [reorderedItem] = stopsReordered.splice(result.source.index, 1)
    stopsReordered.splice(result.destination.index, 0, reorderedItem)

    setValues({
      ...values,
      stops: stopsReordered,
      encodedShape: '',
      distance: ''
    })
  }

  const showCompanyLinesSelector =
    linesByCompany?.companyLines?.length > 1 ||
    values.vertical === GTCompanyV2VerticalInput.City ||
    values.vertical === GTCompanyV2VerticalInput.Travel

  const showClientSelector =
    values.vertical === GTCompanyV2VerticalInput.Team || values.vertical === GTCompanyV2VerticalInput.Rental

  const isDisabledAddStops = showClientSelector && isEmpty(values.businessClients)
  const companyFilter = showClientSelector ? values.company.id : undefined
  const businessClientFilter = showClientSelector ? values.businessClients.at(0)?.id : undefined
  const businessSiteFilter = showClientSelector ? values.businessSites.at(0)?.id : undefined

  return (
    <RouteCardContainer>
      <FormLayout.CardHeader>
        <Heading.H4>
          {title ? (
            title
          ) : (
            <FormattedMessage id="services.form.section.createRoute.title" defaultMessage="Create Route" />
          )}
        </Heading.H4>
      </FormLayout.CardHeader>

      <Row noMargin>
        <Column>
          <Row>
            <Column>
              <Label>
                <FormattedMessage id="routes.new.form.vertical.label" defaultMessage="Vertical" />
              </Label>
              <StyledRadioWrapper>
                {VERTICALS_LIST.map((vertical: GTCompanyV2VerticalInput): ReactElement => {
                  return (
                    <Radio
                      id={vertical}
                      key={vertical}
                      name="vertical"
                      checked={values.vertical === vertical}
                      readOnly={!isEmpty(initialValues.vertical)}
                      onChange={(): void => {
                        setValues({
                          ...values,
                          vertical,
                          company: getDefaultCompanyForForm(login.accessToken),
                          companyLine: { id: '', name: '' },
                          businessSites: [],
                          businessClients: [],
                          stops: [],
                          encodedShape: '',
                          distance: ''
                        })
                      }}
                      disabled={isEditDisabled}
                    >
                      {safeFormatMessage({
                        intl,
                        key: vertical,
                        messages: verticalMessages
                      })}
                    </Radio>
                  )
                })}
              </StyledRadioWrapper>
              {touched.vertical && errors.vertical && <ErrorMessage>{errors.vertical}</ErrorMessage>}
            </Column>
          </Row>
          {canSelectCompany(login.accessToken) && (
            <Row>
              <Column noMargin>
                <CompanyAutocomplete
                  id="company.id"
                  name="company.id"
                  label={intl.formatMessage(messages.companyLabel)}
                  placeholder={intl.formatMessage(messages.companyPlaceholder)}
                  value={values.company.name}
                  onChange={(value: IAutocompleteOutput): void => {
                    setValues({
                      ...values,
                      company: value,
                      companyLine: { id: '', name: '' },
                      businessSites: [],
                      businessClients: [],
                      stops: [],
                      encodedShape: '',
                      distance: ''
                    })
                  }}
                  verticals={[values.vertical as CompanyV2Vertical]}
                  onBlur={handleBlur}
                  touched={getIn(touched, 'company.id')}
                  errors={getIn(errors, 'company.id')}
                  disabled={isEditDisabled}
                />
                {loadingCompanyLinesError && (
                  <ErrorMessage>
                    <FormattedMessage
                      id="routes.new.form.error.loadingCompanyLines"
                      defaultMessage="There was an error while loading the company lines"
                    />
                  </ErrorMessage>
                )}
              </Column>
            </Row>
          )}
          {showCompanyLinesSelector ? (
            <Row>
              <Column noMargin>
                <CompanyLineAutocomplete
                  id="companyLine.id"
                  name="companyLine.id"
                  label={intl.formatMessage(messages.companyLineLabel)}
                  placeholder={intl.formatMessage(messages.companyLinePlaceholder)}
                  value={values.companyLine.name}
                  onChange={(value: IAutocompleteOutput): void => {
                    setFieldValue('companyLine', value)
                  }}
                  onBlur={handleBlur}
                  companyId={values.company.id}
                  touched={getIn(touched, 'companyLine.id')}
                  errors={getIn(errors, 'companyLine.id')}
                  disabled={isEditDisabled}
                />
              </Column>
            </Row>
          ) : null}
          {showClientSelector ? (
            <Row noMargin>
              <Column width="50%">
                <BusinessClientAutocomplete
                  id="client"
                  value={values.businessClients.at(0)?.name || ''}
                  onChange={(businessClient: GTBusinessClientAutocomplete): void => {
                    setValues({
                      ...values,
                      businessClients: [businessClient],
                      businessSites: [],
                      stops: [],
                      encodedShape: '',
                      distance: ''
                    })
                  }}
                  placeholder={intl.formatMessage(messages.clientPlaceholder)}
                  label={<FormattedMessage id="businessSites.form.client.label" defaultMessage="Business client" />}
                  touched={getIn(touched, 'businessClients')}
                  errors={getIn(errors, 'businessClients')}
                  disabled={isEmpty(values.company.id)}
                  companyId={values.company.id}
                />
              </Column>
              <Column>
                <BusinessSiteAutocomplete
                  id="site"
                  businessClientId={values.businessClients.at(0)?.id}
                  value={values.businessSites.at(0)?.name || ''}
                  onChange={(businessSite: GTBusinessSiteSuggestion): void => {
                    setValues({
                      ...values,
                      businessSites: [
                        {
                          id: businessSite.id,
                          name: businessSite.name,
                          clientId: businessSite.businessClient?.id
                        }
                      ],
                      stops: [],
                      encodedShape: '',
                      distance: ''
                    })
                  }}
                  placeholder={intl.formatMessage(messages.sitePlaceholder)}
                  label={<FormattedMessage id="businessSites.form.site.label" defaultMessage="Business site" />}
                  disabled={isEmpty(values.businessClients)}
                />
              </Column>
            </Row>
          ) : null}
        </Column>
        <Column>
          <RouteStopsContainer>
            <Heading.H4 small>
              <FormattedMessage id="routes.new.form.direction.label" defaultMessage="Stops" />
            </Heading.H4>
            <RouteStops
              showLabels={false}
              stops={values.stops as GTPlaceSuggestion[]}
              onChange={onChangeStop}
              onDelete={onDelete}
              handleBlur={handleBlur}
              touched={touched}
              errors={errors}
              handleDragEnd={handleDragEnd}
              companyId={companyFilter}
              businessSiteId={businessSiteFilter}
              businessClientId={businessClientFilter}
              onAddStop={onAddStop}
              showAddStop={!isEditDisabled && !isDisabledAddStops}
              disabled={isEditDisabled}
              vertical={values.vertical}
            />
          </RouteStopsContainer>
          {values.distance ? (
            <p>
              <FormattedMessage
                id="routes.new.form.destination.label"
                defaultMessage="<strong>Kilometers of travel on the route</strong>: <em>{distance} km</em>"
                values={{
                  distance: routeFormUtils.getDistanceLabel(values),
                  strong: (msg) => <strong>{msg}</strong>,
                  em: (msg) => <em>{msg}</em>
                }}
              />
            </p>
          ) : null}
          {errors.encodedShape && touched.encodedShape && (
            <div>
              <ErrorMessage>{errors.encodedShape}</ErrorMessage>
            </div>
          )}
        </Column>
      </Row>
    </RouteCardContainer>
  )
}
